import { isEmpty } from 'lodash';
import { Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import WorkQueues from 'enums/workQueue.enum';
import { getPartners } from 'store/thunks/partnerThunks';
import { workQueueActions } from 'store/slices/workQueueSlice';
import roles from 'common/constants/roles';
import useRoles from 'common/hooks/useRoles';
import { listShippingOptions } from 'store/thunks/batchShipmentThunk';

import WorkQueueTab from './components/WorkQueueTab';
import { WorkQueueTabSwitchProvider } from './contexts/WorkQueueTabSwitchContext';

const NON_WORK_QUEUE_PRESETS = [WorkQueues.RECONCILE_SHIPMENT];

const WorkQueue = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasAuthorizedRoles, viewWorkQueue } = useRoles();
  const rawPreset = useSelector(({ workQueue }) => workQueue.preset);
  const previousPreset = useSelector(({ workQueue }) => workQueue.previousPreset);
  const preset = NON_WORK_QUEUE_PRESETS.includes(rawPreset) ? previousPreset || '' : rawPreset;
  const shippingOptions = useSelector(({ systemConfigs }) => systemConfigs?.shippingOptions);
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);
  const [initialTab, setInitialTab] = useState(preset);
  const isEmptyPartners = isEmpty(partners);
  const isEmptyShippingOptions = isEmpty(shippingOptions);

  useEffect(() => {
    document.title = 'Work Queue - SmartOS';
  }, []);

  useEffect(() => {
    if (isEmptyPartners) {
      dispatch(getPartners());
    }

    if (isEmptyShippingOptions) {
      dispatch(listShippingOptions());
    }
  }, [dispatch, isEmptyPartners, isEmptyShippingOptions]);

  useEffect(() => {
    if (!preset) {
      const getInitialPreset = () => {
        if (hasAuthorizedRoles([roles.admin])) return WorkQueues.RX_INTAKE_REQUESTS;
        if (hasAuthorizedRoles([roles.ccc])) return WorkQueues.BULK_LABELS;
        if (hasAuthorizedRoles([roles.pharmacist])) return WorkQueues.RX_INTAKE_REQUESTS;
        if (hasAuthorizedRoles([roles.billingUser])) return WorkQueues.ORDER_LINE_ITEMS;
        if (hasAuthorizedRoles([roles.pharmTech])) return WorkQueues.RX_INTAKE_REQUESTS;
        if (hasAuthorizedRoles([roles.pharmacyProdTech])) return WorkQueues.BULK_LABELS;
        // Redirect pure shipping-only users (no Work Queue access) to Shipments.
        if (hasAuthorizedRoles([roles.shippingUser]) && !viewWorkQueue) {
          navigate('/shipments', { replace: true });
          return null;
        }
        throw Error('No available queue for this role');
      };
      const initialPreset = getInitialPreset();
      if (initialPreset) {
        setInitialTab(initialPreset);
        dispatch(workQueueActions.loadSelectedWorkQueues(initialPreset));
      }
    }
  }, [dispatch, hasAuthorizedRoles, navigate, preset, viewWorkQueue]);

  return (
    <Grid container sx={{ px: 3, pb: 2 }} direction='column'>
      <Grid item container alignItems='center' spacing={2}>
        <Grid item>
          <Typography component='h1' variant='h5'>
            Work Queues
          </Typography>
        </Grid>
      </Grid>

      <Grid item sx={{ flexgrow: 1, mt: 2 }}>
        {initialTab && (
          <WorkQueueTabSwitchProvider>
            <WorkQueueTab initialTab={initialTab} />
          </WorkQueueTabSwitchProvider>
        )}
      </Grid>
    </Grid>
  );
};

export default WorkQueue;
